import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PortalLogin from './pages/portal/PortalLogin';
import PortalImpersonate from './pages/portal/PortalImpersonate';
import PortalHome from './pages/portal/PortalHome';
import PortalProfile from './pages/portal/PortalProfile';
import PortalCatalog from './pages/portal/PortalCatalog';
import PortalCart from './pages/portal/PortalCart';
import PortalOrders from './pages/portal/PortalOrders';
import { PortalAuthProvider, usePortalAuth } from './context/PortalAuthContext';
import { PortalCartProvider } from './context/PortalCartContext';
import Sales from './pages/Sales';
import RecordSale from './pages/RecordSale';
import CreateInvoice from './pages/CreateInvoice';
import Drafts from './pages/Drafts';
import Products from './pages/Products';
import BarcodeAssignment from './pages/BarcodeAssignment';
import PrintBarcodeLabels from './pages/PrintBarcodeLabels';
import Brands from './pages/Brands';
import Categories from './pages/Categories';
import Customers from './pages/Customers';
import Suppliers from './pages/Suppliers';
import Batches from './pages/Batches';
import Expiry from './pages/Expiry';
import Transactions from './pages/Transactions';
import Dues from './pages/Dues';
import Cheques from './pages/Cheques';
import Reports from './pages/Reports';
import ReportsHub from './pages/ReportsHub';
import SalesReport from './pages/reports/SalesReport';
import InventoryReport from './pages/reports/InventoryReport';
import CustomerStatementReport from './pages/reports/CustomerStatementReport';
import PurchaseReport from './pages/reports/PurchaseReport';
import VatReport from './pages/reports/VatReport';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Letter from './pages/Letter';
import Catalog from './pages/Catalog';
import CloudOrders from './pages/CloudOrders';
import PortalCustomers from './pages/PortalCustomers';
import PreviewInvoice from './pages/PreviewInvoice';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
    const { user, loading, isAdmin } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
                <div className="w-8 h-8 border-4 border-[#C08478] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (adminOnly && !isAdmin()) {
        return <Navigate to="/dashboard" replace />;
    }

    return <Layout>{children}</Layout>;
};

// Public Route - redirects to dashboard if logged in
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A]">
                <div className="w-8 h-8 border-4 border-[#C08478] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (user) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Portal protected route — keeps B2B portal pages behind a portal cookie.
const PortalProtectedRoute = ({ children }) => {
    const { profile, loading } = usePortalAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FBF8F6]">
                <div className="w-8 h-8 border-4 border-[#C08478] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    if (!profile) {
        return <Navigate to="/portal/login" replace />;
    }
    return children;
};

function AdminRoutes() {
    return (
        <Routes>
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                } 
            />
            <Route path="/preview/invoice" element={<PreviewInvoice />} />
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/sales" 
                element={
                    <ProtectedRoute>
                        <Sales />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/sales/record" 
                element={
                    <ProtectedRoute>
                        <RecordSale />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/sales/invoice" 
                element={
                    <ProtectedRoute>
                        <CreateInvoice mode="invoice" />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/sales/quotation" 
                element={
                    <ProtectedRoute>
                        <CreateInvoice mode="quotation" />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/drafts" 
                element={
                    <ProtectedRoute>
                        <Drafts />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/drafts/:id/edit" 
                element={
                    <ProtectedRoute>
                        <CreateInvoice mode="invoice" />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/quotations/:id/edit" 
                element={
                    <ProtectedRoute>
                        <CreateInvoice mode="quotation" />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/products" 
                element={
                    <ProtectedRoute>
                        <Products />
                    </ProtectedRoute>
                } 
            />
            <Route
                path="/products/barcode-assignment"
                element={
                    <ProtectedRoute>
                        <BarcodeAssignment />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/products/print-labels"
                element={
                    <ProtectedRoute>
                        <PrintBarcodeLabels />
                    </ProtectedRoute>
                }
            />
            <Route 
                path="/brands" 
                element={
                    <ProtectedRoute>
                        <Brands />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/categories" 
                element={
                    <ProtectedRoute>
                        <Categories />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/customers" 
                element={
                    <ProtectedRoute>
                        <Customers />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/suppliers" 
                element={
                    <ProtectedRoute>
                        <Suppliers />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/batches" 
                element={
                    <ProtectedRoute>
                        <Batches />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/expiry" 
                element={
                    <ProtectedRoute>
                        <Expiry />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/transactions" 
                element={
                    <ProtectedRoute>
                        <Transactions />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/dues" 
                element={
                    <ProtectedRoute>
                        <Dues />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/cheques" 
                element={
                    <ProtectedRoute>
                        <Cheques />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports" 
                element={
                    <ProtectedRoute>
                        <ReportsHub />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports/sales" 
                element={
                    <ProtectedRoute>
                        <SalesReport />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports/inventory" 
                element={
                    <ProtectedRoute>
                        <InventoryReport />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports/customer-statement" 
                element={
                    <ProtectedRoute>
                        <CustomerStatementReport />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports/purchase" 
                element={
                    <ProtectedRoute>
                        <PurchaseReport />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports/vat" 
                element={
                    <ProtectedRoute>
                        <VatReport />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports/daily-summary" 
                element={
                    <ProtectedRoute>
                        <Reports />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/users" 
                element={
                    <ProtectedRoute adminOnly>
                        <Users />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/settings" 
                element={
                    <ProtectedRoute adminOnly>
                        <Settings />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/letter" 
                element={
                    <ProtectedRoute>
                        <Letter />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/catalog" 
                element={
                    <ProtectedRoute>
                        <Catalog />
                    </ProtectedRoute>
                } 
            />
            <Route
                path="/cloud-orders"
                element={
                    <ProtectedRoute>
                        <CloudOrders />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/portal-customers"
                element={
                    <ProtectedRoute>
                        <PortalCustomers />
                    </ProtectedRoute>
                }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function PortalRoutes() {
    return (
        <Routes>
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/impersonate" element={<PortalImpersonate />} />
            <Route
                path="/portal"
                element={
                    <PortalProtectedRoute>
                        <PortalHome />
                    </PortalProtectedRoute>
                }
            />
            <Route
                path="/portal/profile"
                element={
                    <PortalProtectedRoute>
                        <PortalProfile />
                    </PortalProtectedRoute>
                }
            />
            <Route
                path="/portal/catalog"
                element={
                    <PortalProtectedRoute>
                        <PortalCatalog />
                    </PortalProtectedRoute>
                }
            />
            <Route
                path="/portal/cart"
                element={
                    <PortalProtectedRoute>
                        <PortalCart />
                    </PortalProtectedRoute>
                }
            />
            <Route
                path="/portal/orders"
                element={
                    <PortalProtectedRoute>
                        <PortalOrders />
                    </PortalProtectedRoute>
                }
            />
            <Route path="/" element={<Navigate to="/portal" replace />} />
            <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
    );
}

// Dev / preview mode — both trees mounted, admin default. Used when the
// hostname doesn't match a known production subdomain.
function DevRoutes() {
    return (
        <Routes>
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                } 
            />
            <Route path="/preview/invoice" element={<PreviewInvoice />} />
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/sales" 
                element={
                    <ProtectedRoute>
                        <Sales />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/sales/record" 
                element={
                    <ProtectedRoute>
                        <RecordSale />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/sales/invoice" 
                element={
                    <ProtectedRoute>
                        <CreateInvoice mode="invoice" />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/sales/quotation" 
                element={
                    <ProtectedRoute>
                        <CreateInvoice mode="quotation" />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/drafts" 
                element={
                    <ProtectedRoute>
                        <Drafts />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/drafts/:id/edit" 
                element={
                    <ProtectedRoute>
                        <CreateInvoice mode="invoice" />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/quotations/:id/edit" 
                element={
                    <ProtectedRoute>
                        <CreateInvoice mode="quotation" />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/products" 
                element={
                    <ProtectedRoute>
                        <Products />
                    </ProtectedRoute>
                } 
            />
            <Route
                path="/products/barcode-assignment"
                element={
                    <ProtectedRoute>
                        <BarcodeAssignment />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/products/print-labels"
                element={
                    <ProtectedRoute>
                        <PrintBarcodeLabels />
                    </ProtectedRoute>
                }
            />
            <Route 
                path="/brands" 
                element={
                    <ProtectedRoute>
                        <Brands />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/categories" 
                element={
                    <ProtectedRoute>
                        <Categories />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/customers" 
                element={
                    <ProtectedRoute>
                        <Customers />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/suppliers" 
                element={
                    <ProtectedRoute>
                        <Suppliers />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/batches" 
                element={
                    <ProtectedRoute>
                        <Batches />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/expiry" 
                element={
                    <ProtectedRoute>
                        <Expiry />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/transactions" 
                element={
                    <ProtectedRoute>
                        <Transactions />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/dues" 
                element={
                    <ProtectedRoute>
                        <Dues />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/cheques" 
                element={
                    <ProtectedRoute>
                        <Cheques />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports" 
                element={
                    <ProtectedRoute>
                        <ReportsHub />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports/sales" 
                element={
                    <ProtectedRoute>
                        <SalesReport />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports/inventory" 
                element={
                    <ProtectedRoute>
                        <InventoryReport />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports/customer-statement" 
                element={
                    <ProtectedRoute>
                        <CustomerStatementReport />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports/purchase" 
                element={
                    <ProtectedRoute>
                        <PurchaseReport />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports/vat" 
                element={
                    <ProtectedRoute>
                        <VatReport />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports/daily-summary" 
                element={
                    <ProtectedRoute>
                        <Reports />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/users" 
                element={
                    <ProtectedRoute adminOnly>
                        <Users />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/settings" 
                element={
                    <ProtectedRoute adminOnly>
                        <Settings />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/letter" 
                element={
                    <ProtectedRoute>
                        <Letter />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/catalog" 
                element={
                    <ProtectedRoute>
                        <Catalog />
                    </ProtectedRoute>
                } 
            />
            <Route
                path="/cloud-orders"
                element={
                    <ProtectedRoute>
                        <CloudOrders />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/portal-customers"
                element={
                    <ProtectedRoute>
                        <PortalCustomers />
                    </ProtectedRoute>
                }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/impersonate" element={<PortalImpersonate />} />
            <Route
                path="/portal"
                element={
                    <PortalProtectedRoute>
                        <PortalHome />
                    </PortalProtectedRoute>
                }
            />
            <Route
                path="/portal/profile"
                element={
                    <PortalProtectedRoute>
                        <PortalProfile />
                    </PortalProtectedRoute>
                }
            />
            <Route
                path="/portal/catalog"
                element={
                    <PortalProtectedRoute>
                        <PortalCatalog />
                    </PortalProtectedRoute>
                }
            />
            <Route
                path="/portal/cart"
                element={
                    <PortalProtectedRoute>
                        <PortalCart />
                    </PortalProtectedRoute>
                }
            />
            <Route
                path="/portal/orders"
                element={
                    <PortalProtectedRoute>
                        <PortalOrders />
                    </PortalProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function AppRoutes() {
    // Emergent supports only one custom domain per deployment, so admin
    // + portal are always co-located on the same host. Mount both route
    // trees; react-router picks the matching one by path.
    return <DevRoutes />;
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <PortalAuthProvider>
                    <PortalCartProvider>
                        <AppRoutes />
                        <Toaster position="top-right" richColors />
                    </PortalCartProvider>
                </PortalAuthProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
